import * as React from 'react'
import {
  QueryClient,
  QueryClientProvider,
  useQuery
} from '@tanstack/react-query'
import { Lookup } from '@toasttab/buffet-pui-lookup'
import { Location, getMapboxSearch } from './utils'
import { mapboxPublicToken } from './config'

import { LabeledHelperTextProps } from '@toasttab/buffet-pui-text-base'
import isEqual from 'lodash/isEqual'

export type Address = {
  address1?: string
  address2?: string
  city?: string
  country?: string
  stateCode?: string
  stateName?: string
  administrativeArea?: string
  zipCode?: string
  latitude?: number
  longitude?: number
  // Required for the typecript type that Lookup requires (TODO: Update the types for the lookup component)
  id?: string
}

const formatAddressAsString = (address: Address | null) => {
  return address && address.address1
    ? `${address.address1}${!!address.city ? ', ' + address.city : ''}${
        address.stateCode?.length ? ', ' + address.stateCode : ''
      }, ${address.zipCode}, ${address.country}`
    : ''
}

export type AddressLookupProps = {
  /**
   * A callback for when a selection is made
   */
  onChange?: (selection: Address, inputValue?: string) => void
  /**
   * The selected value (for controlled component - e.g. via Formik)
   */
  value?: Address
  /**
   * A default value (for uncontrolled component)
   */
  defaultValue?: Address | null
  /**
   * @deprecated: setToDefaultValue will be removed in a future version. Please manage the setting and restoring of the default value in your SPA.
   * Allows the value to be reset to the default value (set this to true to reset the value)
   */
  setToDefaultValue?: boolean
  /**
   * The public token for the lookup service (currently mapbox)
   */
  lookupServiceAccessToken?: string
  /**
   * A comma delimited list of countries to include in the address lookup service
   */
  countries?: string
  /**
   * Placeholder text (note that the current address is used as the placeholder text if the value is not empty)
   */
  placeholder?: string
  /**
   * Setting this to true will stop the AddressLookup component from being wrapped in a QueryClientProvider. Use this
   * if you're working on app that already has a react-query QueryClientProvider wrapper.
   */
  hasOwnQueryContext?: boolean
  /**
   * Allows a class name around the whole component for spacing etc
   */
  containerClassName?: string
  /**
   * A method that returns the display version of the given address
   */
  valueToString?: (address: Address | null) => string
  /**
   * Whether to clear the input when an item is not selected from Mapbox suggestions
   */
  clearOnBlur?: boolean
  /**
   * Whether to include the state name in the Address result
   */
  includeStateName?: boolean
  /**
   * A lat/long proximity location to include in the Mapbox lookup request
   */
  proximity?: Location
  /**
   * Whether or not to include Points of Interest in the Mapbox lookup request
   */
  includePOI?: boolean
} & LabeledHelperTextProps

const AddressLookupComponent: React.FC<AddressLookupProps> = ({
  testId = 'address-lookup',
  onChange,
  value = null,
  defaultValue = null,
  setToDefaultValue = false,
  name,
  invalid,
  disabled,
  label,
  containerClassName,
  lookupServiceAccessToken = mapboxPublicToken,
  countries = 'us,ca',
  placeholder = 'Please enter an address',
  errorText,
  helperText,
  helperIconButton,
  required,
  preserveHelpSpace,
  valueToString = formatAddressAsString,
  clearOnBlur = true,
  includeStateName,
  proximity,
  includePOI
}) => {
  // TODO: So weird...really this is all value does?
  const [search, setSearch] = React.useState(value ? valueToString(value) : '')

  React.useEffect(() => {
    if (setToDefaultValue && defaultValue) {
      setSearch(valueToString(defaultValue))
      onChange?.(defaultValue, valueToString(defaultValue))
    }
  }, [setToDefaultValue, defaultValue, setSearch, valueToString, onChange])

  // If the value prop changes, the component should update with the new values
  const prevValueRef = React.useRef(value)
  React.useEffect(() => {
    const prevValue = prevValueRef.current
    const hasValueChanged = !isEqual(prevValue, value)

    if (hasValueChanged && valueToString(value) !== search) {
      setSearch(valueToString(value))
    }
    prevValueRef.current = value
  }, [value, valueToString, search])

  const { data } = useQuery(
    [
      'buffet',
      'address-lookup',
      'mapbox-search',
      search,
      countries,
      includeStateName,
      proximity?.latitude,
      proximity?.longitude,
      includePOI
    ],
    async () =>
      getMapboxSearch(
        search,
        lookupServiceAccessToken,
        countries,
        includeStateName,
        proximity,
        includePOI
      ),
    {
      // Keep the list from flashing in between searches
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  const onClear = () => {
    if (onChange) {
      onChange({}, '')
    }
  }

  return (
    <Lookup<Address>
      testId={testId}
      containerClassName={containerClassName}
      invalid={invalid}
      disabled={disabled}
      errorText={errorText}
      helperText={helperText}
      preserveHelpSpace={preserveHelpSpace}
      name={name}
      required={required}
      label={label}
      helperIconButton={helperIconButton}
      // Weird API, but Lookup doesn't actually require an item
      value={{ input: search, item: undefined }}
      onChange={({ input, item }) => {
        onChange?.(item || {}, input)
        setSearch(input)
      }}
      restoreToItem={clearOnBlur ? defaultValue : undefined}
      placeholder={valueToString(defaultValue) || placeholder}
      items={data || []}
      itemToString={valueToString}
      renderItem={formatAddressAsString}
      onClear={onClear}
    />
  )
}

export const AddressLookup: React.FC<AddressLookupProps> & {
  publicToken: string
} = ({
  testId = 'address-lookup',
  onChange,
  value = null,
  defaultValue = null,
  setToDefaultValue = false,
  hasOwnQueryContext,
  lookupServiceAccessToken = '',
  countries = 'us,ca',
  placeholder = 'Please enter an address',
  valueToString = formatAddressAsString,
  clearOnBlur = true,
  ...restProps
}) => {
  const props = {
    testId,
    onChange,
    value,
    defaultValue,
    setToDefaultValue,
    hasOwnQueryContext,
    lookupServiceAccessToken,
    countries,
    placeholder,
    valueToString,
    clearOnBlur,
    ...restProps
  } as AddressLookupProps

  if (props.hasOwnQueryContext) {
    return <AddressLookupComponent {...props} />
  }
  const queryClient = new QueryClient()
  return (
    // @ts-ignore
    <QueryClientProvider client={queryClient}>
      <AddressLookupComponent {...props} />
    </QueryClientProvider>
  )
}

AddressLookup.publicToken = mapboxPublicToken
